import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { ArrowLeft, Trophy, Crown, Clock, BarChart3, X } from "lucide-react";
import { api } from "../lib/api";
import { ASSETS } from "../lib/constants";

export default function Ranking() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const celebrated = useRef(false);

  useEffect(() => {
    api.get("/public/ranking").then(({ data }) => setData(data)).catch(() => setData({ published: false, ranking: [] }));
  }, []);

  useEffect(() => {
    if (celebrated.current) return;
    if (data?.published && Array.isArray(data.ranking) && data.ranking.length > 0) {
      celebrated.current = true;
      const gold = ["#e9b44c", "#f6d488", "#ffffff", "#c98b4b"];
      const fire = (particleRatio, opts) =>
        confetti({ origin: { y: 0.35 }, colors: gold, disableForReducedMotion: true, particleCount: Math.floor(200 * particleRatio), ...opts });
      const t = setTimeout(() => {
        fire(0.25, { spread: 26, startVelocity: 55 });
        fire(0.2, { spread: 60 });
        fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
        fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
        fire(0.1, { spread: 120, startVelocity: 45 });
      }, 600);
      return () => clearTimeout(t);
    }
  }, [data]);

  return (
    <div className="grain min-h-screen relative">
      <nav className="relative z-20 flex items-center justify-between px-5 sm:px-10 py-5 border-b border-white/10">
        <Link to="/" className="flex items-center gap-3">
          <img src={ASSETS.logo} alt="TSG Sprockhövel" className="h-12 w-auto object-contain" />
          <span className="hidden sm:block text-[11px] tracking-[0.28em] uppercase text-[#9fb2cc]">Tippspiel</span>
        </Link>
        <Link to="/" className="btn-ghost text-xs px-4 py-2 flex items-center gap-2">
          <ArrowLeft size={13} /> Startseite
        </Link>
      </nav>

      <main className="relative z-10 max-w-3xl mx-auto px-5 sm:px-10 py-14 sm:py-20 text-center">
        <p className="text-sm sm:text-base tracking-[0.2em] uppercase text-[#e9b44c] font-bold mb-3 flex items-center justify-center gap-2">
          <Trophy size={18} /> Wochenranking
        </p>
        <h1 className="font-head text-4xl sm:text-6xl uppercase tracking-tight mb-10">Das Ranking</h1>

        {!data && <p className="text-[#9fb2cc]">Wird geladen…</p>}

        {data && !data.published && (
          <div className="glass rounded-3xl p-10 sm:p-14" data-testid="ranking-not-ready">
            <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-[#e9b44c]/15 border border-[#e9b44c]/40 mb-6">
              <Clock size={38} className="text-[#e9b44c]" />
            </div>
            <p className="font-head text-2xl sm:text-3xl uppercase mb-2">Ab Sonntag, 21:00 Uhr</p>
            <p className="text-base text-[#cbd5e1] max-w-md mx-auto">
              Das Wochenranking mit allen Punkten ist verfügbar, sobald die Endergebnisse eingetragen und ausgewertet wurden.
            </p>
          </div>
        )}

        {data && data.published && (
          <div data-testid="ranking-list">
            {data.ranking.length === 0 ? (
              <p className="text-[#9fb2cc]">Noch keine Teilnehmer.</p>
            ) : (
              <>
                <Podium top={data.ranking.slice(0, 3)} onOpen={setSelected} />
                <div className="space-y-3 mt-6">
                  {data.ranking.slice(3).map((r, i) => <Row key={i + 3} r={r} idx={i + 4} onOpen={setSelected} />)}
                </div>
              </>
            )}
          </div>
        )}
      </main>

      <AnimatePresence>
        {selected && <RankingDetailModal player={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </div>
  );
}

const MEDAL = { 0: "#e9b44c", 1: "#cbd5e1", 2: "#c98b4b" };
const medalFor = (rank) => MEDAL[Math.min(Math.max(rank, 1) - 1, 2)];
const barFor = (rank) => ({ 1: "h-36 sm:h-44", 2: "h-24 sm:h-28", 3: "h-20 sm:h-24" }[Math.min(rank, 3)] || "h-20 sm:h-24");

function Podium({ top, onOpen }) {
  const order = top.length === 3 ? [1, 0, 2] : top.map((_, i) => i);
  return (
    <div className="flex items-end justify-center gap-3 sm:gap-5" data-testid="podium">
      {order.map((idx) => {
        const r = top[idx];
        if (!r) return null;
        const rank = r.rank || idx + 1;
        const color = medalFor(rank);
        return (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + idx * 0.12, type: "spring", stiffness: 120 }}
            className="flex flex-col items-center flex-1 max-w-[140px]"
          >
            <div className="flex items-center gap-1 mb-2">
              <span className="font-head text-2xl" style={{ color }}>{rank}</span>
              {r.protagonist && <Crown size={16} style={{ color }} />}
            </div>
            <p className="font-head text-sm sm:text-lg uppercase text-center leading-tight truncate w-full px-1">{r.name}</p>
            <p className="font-head text-2xl sm:text-3xl tabular-nums" style={{ color }}>{String(r.points).replace(".", ",")}</p>
            <div
              className={`${barFor(rank)} w-full rounded-t-2xl mt-2 glass border-t-2 flex items-start justify-center pt-2`}
              style={{ borderTopColor: color, background: rank === 1 ? "rgba(233,180,76,0.12)" : "rgba(255,255,255,0.05)" }}
            >
              <span className="text-[10px] tracking-widest uppercase text-[#9fb2cc]">Pkt</span>
            </div>
            {r.details?.length > 0 && (
              <button onClick={() => onOpen(r)} className="btn-ghost text-[11px] px-3 py-1.5 mt-2 flex items-center gap-1.5" data-testid={`podium-details-${idx}`}>
                <BarChart3 size={13} /> Tipps
              </button>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

function Row({ r, idx, onOpen }) {
  const rank = r.rank || idx;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`glass rounded-2xl px-5 py-4 flex items-center gap-4 ${r.protagonist ? "border-[#e9b44c]/50 bg-[#e9b44c]/10" : ""}`}
      data-testid={`rank-row-${idx}`}
    >
      <span className="font-head text-2xl w-10 shrink-0 text-left text-white/50">{rank}</span>
      <div className="flex-1 text-left flex items-center gap-2 min-w-0">
        <span className="font-head text-xl sm:text-2xl uppercase truncate">{r.name}</span>
        {r.protagonist && (
          <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-[#e9b44c] font-bold shrink-0">
            <Crown size={13} /> Protagonist
          </span>
        )}
      </div>
      {r.details?.length > 0 && (
        <button onClick={() => onOpen(r)} className="btn-ghost text-xs px-3 py-2 flex items-center gap-1.5 shrink-0" data-testid={`row-details-${rank}`}>
          <BarChart3 size={14} /> <span className="hidden sm:inline">Tipps</span>
        </button>
      )}
      <span className="font-head text-2xl sm:text-3xl text-[#e9b44c] tabular-nums shrink-0">{String(r.points).replace(".", ",")}</span>
      <span className="text-[10px] tracking-widest uppercase text-[#9fb2cc] shrink-0">Pkt</span>
    </motion.div>
  );
}

function PointsBadge({ points }) {
  const p = Number(points) || 0;
  const color = p >= 3 ? "#10B981" : p >= 2 ? "#e9b44c" : "#64748b";
  return (
    <span
      className="font-head text-lg tabular-nums px-3 py-1 rounded-full shrink-0"
      style={{ color, background: `${color}22`, border: `1px solid ${color}55` }}
    >
      {String(points).replace(".", ",")}
    </span>
  );
}

function RankingDetailModal({ player, onClose }) {
  const details = player.details || [];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-[#01122f]/80 backdrop-blur-md"
      onClick={onClose}
      data-testid="ranking-detail-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 10 }}
        transition={{ type: "spring", stiffness: 200, damping: 22 }}
        className="glass rounded-3xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-6 border-b border-white/10">
          <div className="min-w-0">
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#e9b44c] font-bold mb-1 flex items-center gap-1.5">
              {player.protagonist && <Crown size={13} />} Tipp-Übersicht
            </p>
            <h3 className="font-head text-2xl sm:text-3xl uppercase leading-none truncate">{player.name}</h3>
          </div>
          <div className="text-right shrink-0">
            <p className="font-head text-3xl text-[#e9b44c] tabular-nums leading-none">{String(player.points).replace(".", ",")}</p>
            <p className="text-[10px] tracking-widest uppercase text-[#9fb2cc] mt-1">Punkte</p>
          </div>
        </div>

        <div className="overflow-y-auto p-5 space-y-3">
          {details.map((d, i) => {
            const hasFinal = d.final_home !== null && d.final_home !== undefined && d.final_away !== null && d.final_away !== undefined;
            return (
              <div key={i} className="rounded-2xl bg-white/[0.04] border border-white/10 p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="text-sm sm:text-base font-semibold text-white truncate">{d.home} <span className="text-[#9fb2cc]">vs</span> {d.away}</p>
                  <PointsBadge points={d.points} />
                </div>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="rounded-xl bg-[#0a2148] py-2.5">
                    <p className="text-[10px] tracking-widest uppercase text-[#9fb2cc] mb-1">Tipp</p>
                    <p className="font-head text-xl tabular-nums text-white">{d.tip_home}:{d.tip_away}</p>
                  </div>
                  <div className="rounded-xl bg-[#0a2148] py-2.5">
                    <p className="text-[10px] tracking-widest uppercase text-[#9fb2cc] mb-1">Endstand</p>
                    <p className="font-head text-xl tabular-nums text-[#e9b44c]">{hasFinal ? `${d.final_home}:${d.final_away}` : "–"}</p>
                  </div>
                </div>
              </div>
            );
          })}
          {details.length === 0 && <p className="text-center text-[#9fb2cc] py-6">Keine Tipps vorhanden.</p>}
        </div>

        <div className="p-5 border-t border-white/10">
          <button onClick={onClose} className="btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2" data-testid="close-detail-modal">
            <X size={16} /> Fenster schließen
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
